import { useUserBookmarkIds, useUserIsSignedIn } from '@src/hooks/user';
import React from 'react';
import toast from 'react-hot-toast';
import { IoIosHeartEmpty, IoMdHeart } from 'react-icons/io';
import * as api from '@src/utils/mmc-api/api';
import { useDispatch } from 'react-redux';
import { fetchUser } from '@src/store/user';
import { login } from '@src/utils/mmc-api/auth';
import posthog from 'posthog-js';

const iconProps = {
  size: 24,
  color: 'hsla(var(--mmc-pink))',
};

type BookmarkButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  id: string;
  showText?: boolean;
};

export const BookmarkButton = ({
  id,
  showText,
  className,
  style,
  ...props
}: BookmarkButtonProps) => {
  const bookMarkedIds = useUserBookmarkIds();
  const isSignedIn = useUserIsSignedIn();
  const dispatch = useDispatch();

  const isBookmarked = bookMarkedIds?.includes(id);

  const updateUser = async () => {
    await api.updateMe();
    dispatch(fetchUser());
  };

  const onAddBookmark = async () => {
    if (!isSignedIn) {
      login();
      return;
    }

    const result = await api.addBookmark({ document_id: id });
    if (result) {
      toast.success('Bookmark added to profile!');
      posthog.capture('bookmark_added', { document_id: id });
      await updateUser();
    }
  };

  const onRemoveBookmark = async () => {
    if (!isSignedIn) {
      login();
      return;
    }

    const result = await api.removeBookmark({ document_id: id });
    if (result) {
      toast.success('Bookmark removed from profile!');
      posthog.capture('bookmark_removed', { document_id: id });
      await updateUser();
    }
  };

  if (isBookmarked) {
    return (
      <button
        onClick={onRemoveBookmark}
        {...props}
        className={`bookmark-button bookmarked ${className || ''}`}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', ...style }}
      >
        {showText && 'Remove from profile'}
        <IoMdHeart {...iconProps} />
      </button>
    );
  }

  return (
    <button
      className={`bookmark-button ${className || ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', ...style }}
      onClick={onAddBookmark}
      {...props}
    >
      {showText && 'Save to profile'}
      <IoIosHeartEmpty {...iconProps} />
    </button>
  );
};
